import React, {useEffect, useState, useCallback} from 'react';
import {CheckBox} from '@components/ui/form/checkbox';
import {useLocation} from 'react-router-dom';
import {IoIosArrowDown, IoIosArrowUp} from 'react-icons/io';
import {Disclosure} from '@headlessui/react';
import Heading from '@components/ui/heading';
import {useTranslation} from 'src/app/i18n/client';
import useQueryParam from '@utils/use-query-params';
import {FilterItem, getFilterNameForLanguage} from "@framework/types";
import {useUI} from "@contexts/ui.context";

export type FilterProps = {
  lang: string;
  queryName: string;
  filterItems: FilterItem[];
  isLoading: boolean;
  error: any;
}

export const Filter = ({lang, filterItems, queryName, isLoading, error}: FilterProps) => {
  const { t } = useTranslation(lang, 'common');
  const location = useLocation();
  const pathname = location.pathname;
  const searchParams = new URLSearchParams(location.search);
  const { updateQueryParams, clearQueryParam } = useQueryParam(pathname ?? '/');
  const [formState, setFormState] = useState<string[]>([]);
  const { closeFilter } = useUI();

  const hasQueryKey = searchParams?.get(queryName);

  const updateQuery = useCallback((value: string[]) => {
    if (value.length === 0) {
      clearQueryParam([queryName]);
    } else {
      updateQueryParams(queryName, value.join(','));
    }
  }, [queryName, updateQueryParams, clearQueryParam]);

  useEffect(() => {
    updateQuery(formState);
  }, [formState, updateQuery]);

  useEffect(() => {
    if (hasQueryKey) {
      setFormState(hasQueryKey.split(','));
    } else {
      setFormState([]);
    }
  }, [hasQueryKey]);

  if (isLoading) return <p>Loading...</p>;
  if (error) return <p>{error.message}</p>;

  function handleItemClick(e: React.FormEvent<HTMLInputElement>): void {
    const { value } = e.currentTarget;
    const newState = formState.includes(value) 
      ? formState.filter(i => i !== value)
      : [...formState, value];
    setFormState(newState);
    closeFilter();
  }

  return (
    <div className="block">
      <Heading className="mb-5 -mt-1">{t(`text-${queryName}-needs`)}</Heading>
      <div className="flex flex-col p-5 border rounded-md border-border-base">
        {filterItems?.slice(0, 3)?.map(item => (
            <CheckBox
                key={`${item.label_en}-key-${item.id}`}
                label={getFilterNameForLanguage(item, lang)}
                name={getFilterNameForLanguage(item, lang).toLowerCase()}
              checked={formState.includes(item.slug)}
              value={item.slug}
              onChange={handleItemClick}
              lang={lang}
            />
          ))}
        {filterItems!.length > 3 && (
          <div className="w-full">
            <Disclosure>
              {({ open }) => (
                <>
                  <Disclosure.Panel className="pt-4 pb-2">
                    {filterItems?.slice(3, filterItems.length).map(item => (
                        <CheckBox
                            key={`${item.label_en}-key-${item.id}`}
                            label={getFilterNameForLanguage(item, lang)}
                            name={getFilterNameForLanguage(item, lang).toLowerCase()}
                          checked={formState.includes(item.slug)}
                          value={item.slug}
                          onChange={handleItemClick}
                          lang={lang}
                        />
                      ))}
                  </Disclosure.Panel>
                  <Disclosure.Button className="flex justify-center items-center w-full px-4 pt-3.5 pb-1 text-sm font-medium text-center text-brand focus:outline-none">
                    {open ? (
                      <>
                        <span className="inline-block ltr:pr-1 rtl:pl-1">
                          {t('text-see-less')}
                        </span>
                        <IoIosArrowUp className="text-brand-dark text-opacity-60 text-15px" />
                      </>
                    ) : (
                      <>
                        <span className="inline-block ltr:pr-1 rtl:pl-1">
                          {t('text-see-more')}
                        </span>
                        <IoIosArrowDown className="text-brand-dark text-opacity-60 text-15px" />
                      </>
                    )}
                  </Disclosure.Button>
                </>
              )}
            </Disclosure>
          </div>
        )}
      </div>
    </div>
  );
};
